






























































































































































































































































import { Vue, Component, Watch } from "@/shared/vuepack";
import { BankCustodian } from "@/admin/types";
import {
  FetchState,
  ContactInformation,
  CitizenshipType,
  EmploymentCategory,
  UserDiscretionaryData
} from "@/shared/types";
import { Validator } from "vee-validate";
import _ from "lodash";
import PoAwareAddress from "@/employee/components/shared/Address.vue";
import Input from "@/shared/components/ui/Input.vue";
import Checkbox from "@/shared/components/ui/Checkbox.vue";
import Button from "@/shared/components/ui/Button.vue";
import ErrorSummary from "@/shared/components/ErrorSummary.vue";
import SelfReportedEmployeeUnclaimed from "@/employee/components/onboarding/SelfReportedEmployeeUnclaimed.vue";
import DuplicateAccountFoundMessage from "@/employee/components/onboarding/DuplicateAccountFoundMessage.vue";
import OtherEnrollmentFoundMessage from "@/employee/components/onboarding/OtherEnrollmentFoundMessage.vue";
import LobAddressSuggestionModal from "@/shared/components/LobAddressSuggestionModal.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import CountrySelector from "@/shared/components/CountrySelector.vue";
import { useUserStore } from "@/shared/store/user";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useEnrollmentStore } from "@/employee/store/enrollment";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserDiscretionaryDataStore } from "@/employee/store/userDiscretionaryDataStore";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

const defaultAddress = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: ""
};

const initialState: ContactInformation = {
  firstName: "",
  middleInitial: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  mobilePhoneNumber: "",
  isMailingSameAsPhysical: false,
  mailingAddress: { ...defaultAddress },
  physicalAddress: { ...defaultAddress },
  lastFourSsn: "",
  memberNumber: "",
  dateOfBirth: "",
  lastLogin: "",
  username: ""
};

const initialDiscretionaryDataState: UserDiscretionaryData = {
  citizenship: CitizenshipType.Unknown,
  citizenshipCountry: "",
  employmentCategory: EmploymentCategory.Unknown,
  employerName: "",
  jobTitle: ""
};

@Component({
  components: {
    ContactAddress: PoAwareAddress,
    Input,
    Checkbox,
    Button,
    ErrorSummary,
    SelfReportedEmployeeUnclaimed,
    LobAddressSuggestionModal,
    GenericLoadingSpinner,
    CountrySelector,
    DuplicateAccountFoundMessage,
    OtherEnrollmentFoundMessage
  }
})
export default class ContactInfo extends Vue {
  $validator: Validator;

  userStore = useUserStore();
  contactInfoStore = useContactInfoStore();
  enrollmentStore = useEnrollmentStore();
  featureFlagStore = useFeatureFlagStore();
  userDiscretionaryDataStore = useUserDiscretionaryDataStore();
  custodianSettingsStore = useCustodianSettingsStore();

  CitizenshipType = CitizenshipType;
  EmploymentCategory = EmploymentCategory;

  isSubmitting = false;
  formModel: ContactInformation = null;
  discretionaryDataFormModel: UserDiscretionaryData = null;
  isLoaded = false;
  isDiscretionaryDataLoaded = false;
  ssn = "";
  ssnConfirm = "";
  dateOfBirth = "";
  isUsCitizen = true;
  canProceedWithNewEmployer = false;


  yesOrNoOptions = [
    { text: "Yes", value: true },
    { text: "No", value: false }
  ];

  get isAllDataLoaded () {
    return this.isLoaded && this.isDiscretionaryDataLoaded;
  }

  get isHsaBankCustodian () {
    return this.custodianSettingsStore.userHsaCustodian === BankCustodian.HsaBank;
  }

  get showMailingAddress () {
    return !this.formModel.isMailingSameAsPhysical;
  }

  get showCitizenshipInfo () {
    return !this.isUsCitizen;
  }

  get showEmploymentDetails () {
    return this.discretionaryDataFormModel.employmentCategory === EmploymentCategory.Employed ||
      this.discretionaryDataFormModel.employmentCategory === EmploymentCategory.SelfEmployed;
  }

  @Watch("contactInfoStore.contactInformation")
  onPlanStateChanged () {
    this.initializeFromStateValue();
  }

  @Watch("userDiscretionaryDataStore.discretionaryData")
  onDiscretionaryDataChanged () {
    this.initializeDiscretionaryDataFromStateValue();
  }

  @Watch("isUsCitizen")
  onIsUsCitizenChanged () {
    if (!this.isUsCitizen && this.discretionaryDataFormModel.citizenship === CitizenshipType.UsCitizen) {
      this.discretionaryDataFormModel.citizenship = CitizenshipType.Unknown;
    }
  }

  isEmptyAddress (address): boolean {
    // when there is no address, the server response is null instead of an object.
    // This is the case i saw during individual onboarding
    if (!address) {
      return true;
    }

    // the reference is non null but the key values are null.
    // This is the case i saw during employee onboarding
    const anyKeyHasValue = Object.keys(address).some(k => !!address[k]);
    return !anyKeyHasValue;
  }

  cloneToCurrentModel (o) {
    const clonedModel = _.cloneDeep(o);

    if (this.isEmptyAddress(clonedModel.mailingAddress)) {
      clonedModel.mailingAddress = { ...defaultAddress };
    }

    if (this.isEmptyAddress(clonedModel.physicalAddress)) {
      clonedModel.physicalAddress = { ...defaultAddress };
    }

    this.formModel = clonedModel;

    if (clonedModel.dateOfBirth) {
      this.dateOfBirth = clonedModel.dateOfBirth;
    } else if (this.enrollmentStore.previouslyEnteredPii && this.enrollmentStore.previouslyEnteredPii.dateOfBirth) {
      this.dateOfBirth = this.enrollmentStore.previouslyEnteredPii.dateOfBirth;
    }

    if (this.enrollmentStore.previouslyEnteredPii && this.enrollmentStore.previouslyEnteredPii.ssn) {
      this.ssn = this.enrollmentStore.previouslyEnteredPii.ssn;
    }

    this.isLoaded = true;
  }

  cloneDiscretionaryDataToModel (o) {
    this.discretionaryDataFormModel = _.cloneDeep(o);
    this.isUsCitizen = this.getInitialCitizenshipSelection();
    this.isDiscretionaryDataLoaded = true;
  }

  getInitialCitizenshipSelection () {
    return this.discretionaryDataFormModel.citizenship === CitizenshipType.UsCitizen ||
      this.discretionaryDataFormModel.citizenship === CitizenshipType.Unknown;
  }

  initializeFromStateValue () {
    const contactInformation = this.contactInfoStore.contactInformation;
    if (this.contactInfoStore.contactInformationState === FetchState.Loaded && !contactInformation) {
      this.cloneToCurrentModel(initialState);
    } else if (contactInformation) {
      this.cloneToCurrentModel(contactInformation);
    } else {
      this.isLoaded = false;
    }
  }

  initializeDiscretionaryDataFromStateValue () {
    this.$validator.pause();

    const discretionaryData = this.userDiscretionaryDataStore.discretionaryData;
    if (this.userDiscretionaryDataStore.fetchState === FetchState.Loaded && !discretionaryData) {
      this.cloneDiscretionaryDataToModel(initialDiscretionaryDataState);
    } else if (discretionaryData) {
      this.cloneDiscretionaryDataToModel(discretionaryData);
    } else {
      this.isDiscretionaryDataLoaded = false;
    }

    this.resetAndResumeValidator();
  }

  resetAndResumeValidator () {
    this.$nextTick(() => {
      this.$validator.reset();
      this.$validator.resume();
    });
  }

  mounted (): void {
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: 0
    });

    this.initializeFromStateValue();
    this.initializeDiscretionaryDataFromStateValue();
    this.fetchData();
  }

  fetchData () {
    this.contactInfoStore.getContactInformation();
    this.userDiscretionaryDataStore.getData();
  }

  copyPhysicalAddressToMailing () {
    if (this.formModel.isMailingSameAsPhysical) {
      this.formModel.mailingAddress = { ...this.formModel.physicalAddress };
    }
  }

  get userEnteredPhysicalZip () {
    const address = this.formModel.physicalAddress;
    return address ? address.zip : "";
  }

  onSubmit (): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.copyPhysicalAddressToMailing();
    this.$validator
        .validate()
        .then(result => {
          if (result) return this.submitValidated();
        })
        .finally(() => this.isSubmitting = false);
  }

  handleSaveContactInformation () {
    return this.contactInfoStore.saveContactInformation(this.formModel)
      .then(() => {
        if (!this.isHsaBankCustodian) {
          return Promise.resolve();
        }

        const request = this.getDiscretionaryDataRequest();
        return this.userDiscretionaryDataStore.updateData(request);
      })
      .then(() => {
        if (this.enrollmentStore.isSelfReportedEmployeeAndUnclaimed) {
          return; // user is stuck until they get attached to their employer
        }

        this.$emit("complete");
      })
      .catch(() => {/* noop */})
      .finally(() => this.isSubmitting = false);
  }

  getDiscretionaryDataRequest (): UserDiscretionaryData {
    const citizenship = this.getCitizenshipTypeForRequest();
    const citizenshipCountry = this.getCitizenshipCountryForRequest(citizenship);
    const employmentCategory = this.discretionaryDataFormModel.employmentCategory;
    const employerName = this.getEmployerNameForRequest(employmentCategory);
    const jobTitle = this.getJobTitleForRequest(employmentCategory);

    return {
      citizenship,
      citizenshipCountry,
      employmentCategory,
      employerName,
      jobTitle
    };
  }

  getCitizenshipTypeForRequest () {
    return this.isUsCitizen ? CitizenshipType.UsCitizen : this.discretionaryDataFormModel.citizenship;
  }

  getCitizenshipCountryForRequest (citizenship: CitizenshipType) {
    return citizenship === CitizenshipType.UsCitizen
      ? ""
      : this.discretionaryDataFormModel.citizenshipCountry;
  }

  getEmployerNameForRequest (employmentCategory: EmploymentCategory) {
    return this.isRetiredOrNotEmployed(employmentCategory)
      ? ""
      : this.discretionaryDataFormModel.employerName;
  }

  getJobTitleForRequest (employmentCategory: EmploymentCategory) {
    return this.isRetiredOrNotEmployed(employmentCategory)
      ? ""
      : this.discretionaryDataFormModel.jobTitle;
  }

  isRetiredOrNotEmployed (employmentCategory: EmploymentCategory) {
    return employmentCategory === EmploymentCategory.Retired || employmentCategory === EmploymentCategory.NotEmployed;
  }

  async submitValidated () {
    this.enrollmentStore.setPreviouslyEnteredPii({
      ssn: this.ssn,
      dateOfBirth: this.dateOfBirth,
      emailAddress: this.formModel.email,
      zip: this.userEnteredPhysicalZip
    });

    // this is broken out to reduce the size of the block in onSubmit
    if (this.userStore.isIndividualAccountType) {
      this.enrollmentStore.resetExistingAccountFlags();
      await this.enrollmentStore.assignCip(
          {
            ssn: this.ssn,
            dateOfBirth: this.dateOfBirth,
            allowSignupWithNewEmployer: this.canProceedWithNewEmployer
          }); // set SSN/dob for individual onboarding
    }

    if (this.useLobApi && !this.contactInfoStore.mailingAddressValidated) {
      return this.contactInfoStore.validateAddress(this.formModel.mailingAddress).then(() => {
        this.formModel.mailingAddress = { ...this.contactInfoStore.lobAddressSuggestion.suggestedAddress };
        this.contactInfoStore.setMailingAddressValidation(true);
        return this.handleSaveContactInformation();
      }).catch(() => {/* noop */
      });
    }
    return this.handleSaveContactInformation();
  }

  onLobAddressSuggestionModalClosed (value: number) {
    this.contactInfoStore.toggleLobSuggestionModal(false);
    if (value > 0) {
      this.isSubmitting = true;
      if (value === 2) {
        this.formModel.mailingAddress = { ...this.contactInfoStore.lobAddressSuggestion.suggestedAddress };
      }
      this.contactInfoStore.setMailingAddressValidation(true);
      this.handleSaveContactInformation();
    }
  }

  handleAddressValidation (value: boolean, formId: string) {
    if (formId === "mailingAddress") {
      this.contactInfoStore.setMailingAddressValidation(value);
    }
  }

  get useLobApi () {
    return this.featureFlagStore.flags.lobAddressValidationIsEnabled;
  }

  onSsnChange () {
    this.canProceedWithNewEmployer = false;
  }

  proceedWithNewEmployer () {
    this.canProceedWithNewEmployer = true;
    this.onSubmit();
  }

  get showDuplicateAccountFoundMessage () {
    return this.enrollmentStore.hasExistingAccount;
  }

  get showOtherEnrollmentFoundMessage () {
    return this.enrollmentStore.hasAccountWithOtherEmployer && !this.canProceedWithNewEmployer;
  }
}
